// src/components/PendenzEditModal.jsx
// Wiederverwendbares Bearbeiten-Modal für eine einzelne Pendenz (z.B. aus dem
// Terminplan geöffnet). Lädt selbst die nötigen Daten (Verantwortlichen-Optionen,
// Kategorien, Projekt-Zuordnung) und nutzt die geteilten Formularfelder aus
// Pendenzen.jsx. Bearbeitungsrecht wie US-PEND-008 (Admin/PL oder zugeordnet).
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  updatePendenz,
  deletePendenz,
  getCategories,
  FIXED_CATEGORIES,
} from "../services/azurePendenzenService";
import { getAllPartners } from "../services/azurePartnersService";
import { getAllPersons } from "../services/azurePersonsService";
import { getProjectRelations } from "../services/azureRelationsService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getProjectAssignments } from "../services/azureProjectAssignmentService";
import {
  PendenzFields,
  buildResponsibleOptions,
  verantwortlichDisplay,
  computeVerantwortlich,
  computeVerantwortlichExtern,
} from "./Pendenzen";
import { isRichTextEmpty } from "../utils/richText";
import PendenzResponsibleCreateModal from "./PendenzResponsibleCreateModal";
import PendenzResponsibleLinkModal from "./PendenzResponsibleLinkModal";
import { useAuth } from "../hooks/useAuth";
import { hasRole, ROLES } from "../utils/permissions";
import "../styles/pendenzen.scss";

const todayIso = () => {
  const d = new Date();
  return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const seedForm = (p) => ({
  typ: p.typ || "P",
  kategorie: p.kategorie || "Org",
  beschreibung: p.beschreibung || "",
  verantwortlichIds: p.verantwortlichIds || [],
  // Fallback nur für echte Altdaten (nie IDs gehabt); sonst leer, damit ein
  // geleertes Verantwortlich-Feld nicht den internen Namen extern durchschlägt.
  verantwortlichFallback: p.verantwortlichIds?.length ? "" : p.verantwortlich || "",
  terminSoll: p.terminSoll || "",
  terminNeu: p.terminNeu || "",
  status: p.status || "offen",
  kommentar: p.kommentar || "",
  checkliste: p.checkliste || [],
  themen: p.themen || [],
  sichtbarExtern: !!p.sichtbarExtern,
  sitzungId: p.sitzungId || "",
});

const PendenzEditModal = ({
  pendenz,
  projectUuid,
  projectCode,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [persons, setPersons] = useState([]);
  const [relations, setRelations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(() => seedForm(pendenz));
  const [crmModal, setCrmModal] = useState(null);
  const [linkModal, setLinkModal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [partnersData, personsData, relationsData, teamData, catData, assignData] =
          await Promise.all([
            getAllPartners(),
            getAllPersons(),
            getProjectRelations(projectCode),
            getAllTeamMembers(),
            getCategories(projectUuid),
            getProjectAssignments(projectCode),
          ]);
        if (cancelled) return;
        setPartners(partnersData);
        setPersons(personsData);
        setRelations(relationsData);
        setTeamMembers(teamData);
        setCustomCategories(catData);
        setAssignments(assignData);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Daten konnten nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectUuid, projectCode]);

  const categories = useMemo(
    () => [
      ...FIXED_CATEGORIES.map((c) => ({ id: c.id, label: c.label, fixed: true })),
      ...customCategories.map((c) => ({ id: c.id, label: c.label, fixed: false })),
    ],
    [customCategories]
  );

  const responsibleOptions = useMemo(
    () => buildResponsibleOptions(partners, persons, relations, teamMembers),
    [partners, persons, relations, teamMembers]
  );

  const crmOptions = useMemo(() => {
    const assigned = new Set(responsibleOptions.map((o) => o.id));
    const orgs = partners.map((p) => ({
      id: `org:${p.id}`,
      label: p.name,
      group: "Organisation",
    }));
    const pers = persons.map((p) => ({
      id: `person:${p.id}`,
      label:
        `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.email || "(ohne Name)",
      group: "Person",
    }));
    return [...orgs, ...pers].filter((o) => !assigned.has(o.id));
  }, [partners, persons, responsibleOptions]);

  const isAdmin = hasRole(user, ROLES.ADMIN);
  const isPL = hasRole(user, ROLES.PL);
  const canEdit = useMemo(() => {
    if (isAdmin || isPL) return true;
    const uuid = user?.teamMember?.employeeUuid;
    return !!uuid && assignments.some((a) => a.employeeUuid === uuid);
  }, [isAdmin, isPL, user, assignments]);
  const readOnly = !canEdit;

  const change = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleCreateRequest = (createType, name, addFn) =>
    setCrmModal({ type: createType, name, addFn });
  const handleResponsibleCreated = ({ type, entity, relation, addSelect }) => {
    if (type === "person") setPersons((prev) => [...prev, entity]);
    else setPartners((prev) => [...prev, entity]);
    if (relation) setRelations((prev) => [...prev, relation]);
    addSelect?.();
    setCrmModal(null);
  };
  const handleLinkExisting = (option, addFn) => setLinkModal({ option, addFn });
  const handleLinked = (relation) => {
    if (relation) setRelations((prev) => [...prev, relation]);
    linkModal?.addFn?.(linkModal.option.id);
    setLinkModal(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isRichTextEmpty(form.beschreibung)) {
      setError("Bitte eine Beschreibung erfassen.");
      return;
    }
    const status = form.status;
    const erledigtAm =
      status === "erledigt" ? pendenz.erledigtAm || todayIso() : "";
    const updated = {
      ...pendenz,
      typ: form.typ,
      kategorie: form.kategorie,
      beschreibung: form.beschreibung,
      verantwortlichIds: form.verantwortlichIds,
      verantwortlich: computeVerantwortlich(
        form.verantwortlichIds,
        responsibleOptions,
        form.verantwortlichFallback
      ),
      verantwortlichExtern: computeVerantwortlichExtern(
        form.verantwortlichIds,
        responsibleOptions,
        form.verantwortlichFallback
      ),
      terminSoll: form.terminSoll,
      terminNeu: form.terminNeu,
      status,
      erledigtAm,
      kommentar: form.kommentar,
      checkliste: form.checkliste,
      themen: form.themen || [],
      sichtbarExtern: form.sichtbarExtern,
      sitzungId: form.sitzungId || "",
    };
    try {
      setSaving(true);
      setError(null);
      await updatePendenz(projectUuid, pendenz.id, updated);
      onSaved?.(updated);
    } catch (err) {
      setError(err?.message || "Die Pendenz konnte nicht gespeichert werden.");
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Pendenz Nr. ${pendenz.nummer} wirklich löschen?`)) return;
    try {
      setSaving(true);
      setError(null);
      await deletePendenz(projectUuid, pendenz.id);
      onDeleted?.(pendenz.id);
    } catch (err) {
      setError(err?.message || "Die Pendenz konnte nicht gelöscht werden.");
      setSaving(false);
    }
  };

  return (
    <div className="pendenzen">
      {/* Weder Klick auf den Hintergrund noch Escape schliessen (Nutzerwunsch, ADR
          0060): kein versehentliches Verwerfen ungespeicherter Eingaben.
          Schliessen nur über X/Abbrechen/Speichern. */}
      <div
        className="pendenzen__modal-overlay"
        onKeyDown={(e) => {
          if (e.key === "Escape") e.stopPropagation();
        }}
      >
        <div className="pendenzen__modal" onClick={(e) => e.stopPropagation()}>
          <form onSubmit={handleSave}>
            <div className="pendenzen__modal-header">
              <h3>
                Pendenz Nr. {pendenz.nummer}
                {readOnly ? " ansehen" : " bearbeiten"}
              </h3>
              <button
                type="button"
                className="pendenzen__modal-close"
                onClick={onClose}
                aria-label="Schliessen"
              >
                ×
              </button>
            </div>

            <div className="pendenzen__modal-body">
              {error && (
                <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
                  {error}
                </div>
              )}
              {loading ? (
                <div className="pendenzen__share-empty">Daten werden geladen…</div>
              ) : (
                <fieldset
                  disabled={readOnly}
                  style={{ border: "none", padding: 0, margin: 0, minInlineSize: "auto" }}
                >
                  <PendenzFields
                    form={form}
                    onChange={change}
                    options={responsibleOptions}
                    onCreateRequest={handleCreateRequest}
                    crmOptions={crmOptions}
                    onLinkExisting={handleLinkExisting}
                    categoryOptions={categories}
                    showStatus
                    readOnly={readOnly}
                  />
                </fieldset>
              )}
            </div>

            <div className="pendenzen__modal-footer">
              {!readOnly && (
                <button
                  type="button"
                  className="pendenzen__btn pendenzen__btn--danger"
                  onClick={handleDelete}
                  disabled={saving || loading}
                >
                  Löschen
                </button>
              )}
              <div className="pendenzen__modal-footer-right">
                <button
                  type="button"
                  className="pendenzen__btn"
                  onClick={onClose}
                  disabled={saving}
                >
                  {readOnly ? "Schliessen" : "Abbrechen"}
                </button>
                {!readOnly && (
                  <button
                    type="submit"
                    className="pendenzen__btn pendenzen__btn--primary"
                    disabled={saving || loading}
                  >
                    {saving ? "Speichern..." : "Speichern"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {crmModal && (
        <PendenzResponsibleCreateModal
          type={crmModal.type}
          initialName={crmModal.name}
          partners={partners}
          projectCode={projectCode}
          onClose={() => setCrmModal(null)}
          onCreated={({ type, entity, relation, option }) =>
            handleResponsibleCreated({
              type,
              entity,
              relation,
              addSelect: () => crmModal.addFn?.(option.id),
            })
          }
        />
      )}
      {linkModal && (
        <PendenzResponsibleLinkModal
          option={linkModal.option}
          projectCode={projectCode}
          onClose={() => setLinkModal(null)}
          onLinked={handleLinked}
        />
      )}
    </div>
  );
};

export default PendenzEditModal;
